import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useSEO } from "../lib/seo";
import RichText from "../components/RichText";
import VinylCard from "../components/VinylCard";

const fmt = (iso, lang) =>
  iso ? new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z").toLocaleDateString(lang === "en" ? "en-GB" : "es-ES", { day: "numeric", month: "long", year: "numeric" }) : "";

export function ArticleList() {
  const { lang } = useI18n();
  const [items, setItems] = useState(null);
  useSEO({ title: lang === "en" ? "Recommended · Maggie Records" : "Artículos recomendados · Maggie Records" });

  useEffect(() => {
    api.get("/articles").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);

  return (
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-10 md:pt-14" data-testid="articles-page">
      <div className="border-b border-border pb-5 mb-10">
        <h1 className="text-2xl md:text-4xl font-medium">{lang === "en" ? "Recommended." : "Artículos recomendados."}</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
          {lang === "en" ? "Picks, stories and tips from the shop." : "Selecciones, historias y consejos desde la tienda."}
        </p>
      </div>
      {!items ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="aspect-[4/3] bg-[hsl(var(--muted))] animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{lang === "en" ? "No articles yet." : "Aún no hay artículos."}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
          {items.map((a) => <ArticleCard key={a.id} article={a} lang={lang} />)}
        </div>
      )}
    </div>
  );
}

export function ArticleCard({ article, lang }) {
  return (
    <Link to={`/articulos/${article.slug}`} className="group block" data-testid={`article-card-${article.slug}`}>
      <div className="aspect-[4/3] bg-[hsl(var(--muted))] overflow-hidden mb-4">
        <img
          src={article.cover_image || "/brand/cover-placeholder.png"}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
        />
      </div>
      <p className="text-xs text-muted-foreground mb-1.5">{fmt(article.published_at, lang)}</p>
      <h2 className="text-lg font-medium leading-snug group-hover:text-[hsl(var(--accent))] transition-colors">{article.title}</h2>
      {article.excerpt && <p className="text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-3">{article.excerpt}</p>}
    </Link>
  );
}

export function ArticleDetail() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const [article, setArticle] = useState(undefined);

  useEffect(() => {
    setArticle(undefined);
    api.get(`/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setArticle(null));
  }, [slug]);

  useSEO(article ? { title: `${article.title} · Maggie Records`, description: article.excerpt, image: article.cover_image || article.listings?.[0]?.cover_image } : { title: "Maggie Records" });

  if (article === undefined) return <div className="max-w-[860px] mx-auto px-5 pt-14"><div className="h-10 w-2/3 bg-[hsl(var(--muted))] animate-pulse" /></div>;
  if (article === null) {
    return (
      <div className="max-w-[860px] mx-auto px-5 md:px-10 pt-14">
        <h1 className="text-2xl font-medium mb-4">{lang === "en" ? "Article not found" : "Artículo no encontrado"}</h1>
        <Link to="/articulos" className="vmr-link-underline text-sm">{lang === "en" ? "All articles →" : "Ver todos los artículos →"}</Link>
      </div>
    );
  }

  return (
    <article className="max-w-[1600px] mx-auto px-5 md:px-10 pt-10 md:pt-14" data-testid="article-detail">
      <div className="max-w-[760px]">
        <Link to="/articulos" className="text-xs text-muted-foreground hover:text-foreground">← {lang === "en" ? "Recommended" : "Artículos"}</Link>
        <p className="text-xs text-muted-foreground mt-6 mb-2">{fmt(article.published_at, lang)}</p>
        <h1 className="text-3xl md:text-5xl font-medium leading-tight">{article.title}</h1>
        {article.excerpt && <p className="text-base md:text-lg text-muted-foreground mt-4 leading-relaxed">{article.excerpt}</p>}
        {article.cover_image && <img src={article.cover_image} alt="" className="w-full aspect-[16/9] object-cover mt-8" />}
        <RichText text={article.body} className="text-sm md:text-base leading-relaxed text-foreground/90 mt-8" />
      </div>

      {article.listings?.length > 0 && (
        <section className="mt-16 border-t border-border pt-8" data-testid="article-listings">
          <h2 className="text-sm font-medium mb-6">{lang === "en" ? "Records in this article" : "Discos de este artículo"}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-4 gap-y-8 md:gap-x-5 md:gap-y-10">
            {article.listings.map((it) => <VinylCard key={it.listing_id} item={it} />)}
          </div>
        </section>
      )}
    </article>
  );
}
